// src/components/admin/settings/PrivacySettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Lock, Shield, Users, Database, Plus, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { 

  updatePrivacyPolicy, 
  selectPrivacyPolicy, 
  selectSettingsUpdating 
} from '../../../store/slices/platformSettingsSlice';

const PrivacySettings = () => {
  const dispatch = useDispatch();
  const privacyPolicyRedux = useSelector(selectPrivacyPolicy);
  const isSaving = useSelector(selectSettingsUpdating);

  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: platformName } = useTranslation('Platform Name');
  const { translatedText: version } = useTranslation('Version');
  const { translatedText: active } = useTranslation('Active');
  const { translatedText: changesSummary } = useTranslation('Changes Summary');
  const { translatedText: gdprCompliant } = useTranslation('GDPR Compliant');
  const { translatedText: cookieConsentRequired } = useTranslation('Cookie Consent Required');
  const { translatedText: dataRetentionPeriod } = useTranslation('Data Retention Period (days)');
  const { translatedText: collectLocation } = useTranslation('Collect Location Data');
  const { translatedText: collectAnalytics } = useTranslation('Collect Analytics');
  const { translatedText: collectCookies } = useTranslation('Collect Cookies');
  const { translatedText: rightToAccess } = useTranslation('Right to Access');
  const { translatedText: rightToRectification } = useTranslation('Right to Rectification');
  const { translatedText: rightToErasure } = useTranslation('Right to Erasure');
  const { translatedText: rightToObject } = useTranslation('Right to Object');
  const { translatedText: rightToDataPortability } = useTranslation('Right to Data Portability');
  const { translatedText: privacyContent } = useTranslation('Privacy Content');
  const { translatedText: addSection } = useTranslation('Add Section');
  const { translatedText: sectionTitle } = useTranslation('Section Title');
  const { translatedText: sectionContent } = useTranslation('Section Content');
  const { translatedText: removeSection } = useTranslation('Remove Section');
  const { translatedText: complianceSettings } = useTranslation('Compliance Settings');
  const { translatedText: dataCollection } = useTranslation('Data Collection');
  const { translatedText: userRights } = useTranslation('User Rights');
  const { translatedText: saveChanges } = useTranslation('Save Changes');
  const { translatedText: savingText } = useTranslation('Saving...');

  const [privacyData, setPrivacyData] = useState({
    title: '',
    version: '1.0.0',
    isActive: true,
    sections: [],
    translations: {
      fr: { title: '', sections: [] },
      ht: { title: '', sections: [] }
    },
    compliance: {
      gdprCompliant: true,
      dataRetentionPeriod: 365,
      cookieConsentRequired: true,
      dataProcessingAgreement: ''
    },
    dataCollection: {
      collectLocation: true,
      collectAnalytics: true,
      collectCookies: true
    },
    userRights: {
      rightToAccess: true,
      rightToRectification: true,
      rightToErasure: true,
      rightToObject: true,
      rightToDataPortability: true
    },
    changesSummary: ''
  });

  const [isInitialLoad, setIsInitialLoad] = useState(true);



  // Update local state when Redux data changes - FIXED: prevents infinite loop
  useEffect(() => {
    if (privacyPolicyRedux && Object.keys(privacyPolicyRedux).length > 0) {
      setPrivacyData({
        title: privacyPolicyRedux.title || 'Privacy Policy',
        version: privacyPolicyRedux.version || '1.0.0',
        isActive: privacyPolicyRedux.isActive !== undefined ? privacyPolicyRedux.isActive : true,
        sections: privacyPolicyRedux.content?.sections || [],
        translations: privacyPolicyRedux.translations || {
          fr: { title: '', sections: [] },
          ht: { title: '', sections: [] }
        },
        compliance: privacyPolicyRedux.compliance || {
          gdprCompliant: true,
          dataRetentionPeriod: 365,
          cookieConsentRequired: true,
          dataProcessingAgreement: ''
        },
        dataCollection: privacyPolicyRedux.dataCollection || {
          collectLocation: true,
          collectAnalytics: true,
          collectCookies: true
        },
        userRights: privacyPolicyRedux.userRights || {
          rightToAccess: true,
          rightToRectification: true,
          rightToErasure: true,
          rightToObject: true,
          rightToDataPortability: true
        },
        changesSummary: ''
      });
      setIsInitialLoad(false);
    }
  }, [privacyPolicyRedux]);

  // Section management - wrapped in useCallback
  const addSectionHandler = useCallback(() => {
    setPrivacyData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        { title: `Section ${prev.sections.length + 1}`, content: '', order: prev.sections.length + 1 }
      ]
    }));
  }, []);

  const removeSectionHandler = useCallback((index) => {
    setPrivacyData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  }, []);

  const updateSection = useCallback((index, field, value) => {
    setPrivacyData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }));
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const updateData = {
        title: privacyData.title,
        version: privacyData.version,
        isActive: privacyData.isActive,
        content: { sections: privacyData.sections },
        translations: privacyData.translations,
        compliance: privacyData.compliance,
        dataCollection: privacyData.dataCollection,
        userRights: privacyData.userRights,
        changesSummary: privacyData.changesSummary || 'Updated Privacy Policy'
      };
      
      await dispatch(updatePrivacyPolicy(updateData)).unwrap();
      toast.success('Privacy Policy updated successfully!');
     
    } catch (error) {
      toast.error(error || 'Failed to update Privacy Policy');
    }
  }, [dispatch, privacyData]);

  if (isInitialLoad && !privacyPolicyRedux) {
    return (
      <div className={styles.settingsCard}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading Privacy Policy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settingsCard}>
      <h3>
        <Lock size={20} />
        {privacyPolicy || 'Privacy Policy'}
      </h3>
      
      <div className={styles.settingsForm}>
        {/* Basic Info */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{platformName || 'Title'}</label>
            <input
              type="text"
              value={privacyData.title}
              onChange={(e) => setPrivacyData({ ...privacyData, title: e.target.value })}
              placeholder="Privacy Policy"
              disabled={isSaving}
            />
          </div>
          <div className={styles.formGroup}>
            <label>{version || 'Version'}</label>
            <input
              type="text"
              value={privacyData.version}
              onChange={(e) => setPrivacyData({ ...privacyData, version: e.target.value })}
              placeholder="1.0.0"
              disabled={isSaving}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.isActive}
                onChange={(e) => setPrivacyData({ ...privacyData, isActive: e.target.checked })}
                disabled={isSaving}
              />
              {active || 'Active'}
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>{changesSummary || 'Changes Summary'}</label>
          <input
            type="text"
            value={privacyData.changesSummary}
            onChange={(e) => setPrivacyData({ ...privacyData, changesSummary: e.target.value })}
            placeholder="Summary of changes made"
            disabled={isSaving}
          />
        </div>

        {/* Compliance Settings */}
        <h4 className={styles.subsectionTitle}>
          <Shield size={16} />
          {complianceSettings || 'Compliance Settings'}
        </h4>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.compliance.gdprCompliant}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  compliance: { ...privacyData.compliance, gdprCompliant: e.target.checked }
                })}
                disabled={isSaving}
              />
              {gdprCompliant || 'GDPR Compliant'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.compliance.cookieConsentRequired}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  compliance: { ...privacyData.compliance, cookieConsentRequired: e.target.checked }
                })}
                disabled={isSaving}
              />
              {cookieConsentRequired || 'Cookie Consent Required'}
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>{dataRetentionPeriod || 'Data Retention Period (days)'}</label>
          <input
            type="number"
            value={privacyData.compliance.dataRetentionPeriod}
            onChange={(e) => setPrivacyData({
              ...privacyData,
              compliance: { ...privacyData.compliance, dataRetentionPeriod: Number(e.target.value) }
            })}
            min="1"
            disabled={isSaving}
          />
        </div>

        <div className={styles.formGroup}>
          <label>Data Processing Agreement</label>
          <textarea
            value={privacyData.compliance.dataProcessingAgreement}
            onChange={(e) => setPrivacyData({
              ...privacyData,
              compliance: { ...privacyData.compliance, dataProcessingAgreement: e.target.value }
            })}
            rows={2}
            placeholder="Describe how data is processed..."
            disabled={isSaving}
          />
        </div>

        {/* Data Collection */}
        <h4 className={styles.subsectionTitle}>
          <Database size={16} />
          {dataCollection || 'Data Collection'}
        </h4>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.dataCollection.collectLocation}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  dataCollection: { ...privacyData.dataCollection, collectLocation: e.target.checked }
                })}
                disabled={isSaving}
              />
              {collectLocation || 'Collect Location Data'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.dataCollection.collectAnalytics}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  dataCollection: { ...privacyData.dataCollection, collectAnalytics: e.target.checked }
                })}
                disabled={isSaving}
              />
              {collectAnalytics || 'Collect Analytics'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.dataCollection.collectCookies}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  dataCollection: { ...privacyData.dataCollection, collectCookies: e.target.checked }
                })}
                disabled={isSaving}
              />
              {collectCookies || 'Collect Cookies'}
            </label>
          </div>
        </div>

        {/* User Rights */}
        <h4 className={styles.subsectionTitle}>
          <Users size={16} />
          {userRights || 'User Rights'}
        </h4>

        <div className={styles.rightsGrid}>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.userRights.rightToAccess}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  userRights: { ...privacyData.userRights, rightToAccess: e.target.checked }
                })}
                disabled={isSaving}
              />
              {rightToAccess || 'Right to Access'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.userRights.rightToRectification}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  userRights: { ...privacyData.userRights, rightToRectification: e.target.checked }
                })}
                disabled={isSaving}
              />
              {rightToRectification || 'Right to Rectification'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.userRights.rightToErasure}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  userRights: { ...privacyData.userRights, rightToErasure: e.target.checked }
                })}
                disabled={isSaving}
              />
              {rightToErasure || 'Right to Erasure'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.userRights.rightToObject}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  userRights: { ...privacyData.userRights, rightToObject: e.target.checked }
                })}
                disabled={isSaving}
              />
              {rightToObject || 'Right to Object'}
            </label>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={privacyData.userRights.rightToDataPortability}
                onChange={(e) => setPrivacyData({
                  ...privacyData,
                  userRights: { ...privacyData.userRights, rightToDataPortability: e.target.checked }
                })}
                disabled={isSaving}
              />
              {rightToDataPortability || 'Right to Data Portability'}
            </label>
          </div>
        </div>

        {/* Privacy Sections */}
        <div className={styles.sectionsHeader}>
          <h4>{privacyContent || 'Privacy Content'}</h4>
          <button className={styles.addSectionBtn} onClick={addSectionHandler} disabled={isSaving}>
            <Plus size={16} />
            {addSection || 'Add Section'}
          </button>
        </div>

        {privacyData.sections.map((section, index) => (
          <div key={index} className={styles.sectionItem}>
            <div className={styles.sectionHeader}>
              <span>Section {index + 1}</span>
              <button 
                className={styles.removeSectionBtn}
                onClick={() => removeSectionHandler(index)}
                disabled={isSaving}
              >
                <X size={16} />
              </button>
            </div>
            <div className={styles.formGroup}>
              <label>{sectionTitle || 'Section Title'}</label>
              <input
                type="text"
                value={section.title}
                onChange={(e) => updateSection(index, 'title', e.target.value)}
                placeholder="Enter section title"
                disabled={isSaving}
              />
            </div>
            <div className={styles.formGroup}>
              <label>{sectionContent || 'Section Content'}</label>
              <textarea
                value={section.content}
                onChange={(e) => updateSection(index, 'content', e.target.value)}
                rows={3}
                placeholder="Enter section content"
                disabled={isSaving}
              />
            </div>
          </div>
        ))}

        <button 
          className={styles.saveBtn} 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (savingText || 'Saving...') : (saveChanges || 'Save Changes')}
        </button>
      </div>
    </div>
  );
};

export default PrivacySettings;